import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processSessionId } = useAuth();
  const { isDarkMode } = useTheme();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash or query params
        const hash = location.hash;
        const search = location.search;
        
        let sessionId = null;
        
        // Try hash first (most common)
        const hashMatch = hash.match(/session_id=([^&]+)/);
        if (hashMatch) {
          sessionId = hashMatch[1];
        }
        
        // Try query params as fallback
        if (!sessionId) {
          const params = new URLSearchParams(search);
          sessionId = params.get('session_id');
        }
        
        if (sessionId) {
          setStatus('authenticating');
          const success = await processSessionId(sessionId);
          
          if (success) {
            setStatus('success');
            // Small delay to show success message
            setTimeout(() => {
              // Clear the URL and navigate to home
              window.history.replaceState(null, '', '/');
              navigate('/', { replace: true });
            }, 500);
          } else {
            setStatus('error');
            setError('Authentication failed. Please try again.');
            setTimeout(() => {
              navigate('/settings', { replace: true });
            }, 2000);
          }
        } else {
          setStatus('error');
          setError('No session found. Please try signing in again.');
          setTimeout(() => {
            navigate('/settings', { replace: true });
          }, 2000);
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setError('An error occurred. Please try again.');
        setTimeout(() => {
          navigate('/settings', { replace: true });
        }, 2000);
      }
    };

    processAuth();
  }, [location.hash, location.search, navigate, processSessionId]);

  return (
    <div className={`min-h-screen flex items-center justify-center ${
      isDarkMode 
        ? 'bg-gradient-to-b from-[#0f172a] to-[#1e293b]' 
        : 'bg-gradient-to-b from-sky-100 to-white'
    }`}>
      <div className="text-center p-6">
        {status === 'processing' || status === 'authenticating' ? (
          <>
            <div className={`w-12 h-12 border-4 ${
              isDarkMode ? 'border-amber-400' : 'border-[#1a365d]'
            } border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
              {status === 'authenticating' ? 'Signing you in...' : 'Processing...'}
            </p>
          </>
        ) : status === 'success' ? (
          <>
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className={isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}>
              Sign in successful!
            </p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className={isDarkMode ? 'text-red-400' : 'text-red-600'}>
              {error || 'Something went wrong'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
